import { useState, useRef, useEffect, useCallback } from "react";
import { useTheme } from "../context/ThemeContext";
import { authHeaders, handleAuthFailure } from "../data/api";

// Same convention as src/data/api.js — BASE already includes the /api prefix.
const BASE = process.env.REACT_APP_API_URL || "http://localhost:4000/api";

export default function PromoManagement({ channel, viewOnly = false, onBack, onCountChange }) {
  const { mode } = useTheme();
  const [promos, setPromos]                   = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [dragOver, setDragOver]               = useState(false);
  const [uploading, setUploading]             = useState(false);
  const [uploadLabel, setUploadLabel]         = useState("");
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [deleteTarget, setDeleteTarget]       = useState(null);
  const [playingPromo, setPlayingPromo]       = useState(null);
  const [videoUrl, setVideoUrl]               = useState(null);
  const [videoLoading, setVideoLoading]       = useState(false);
  const [videoError, setVideoError]           = useState(null);
  const [error, setError]                     = useState(null);
  const fileInputRef  = useRef(null);
  const pollTimerRef  = useRef(null);

  // ── Load promos from API ──────────────────────────────────────────────────
  const loadPromos = useCallback(async () => {
    if (!channel?.id) return;
    try {
      const res  = await fetch(`${BASE}/channels/${channel.id}/promos`, { headers: authHeaders() });
      // This is a raw fetch, not the request() helper, so wrap the status in
      // the { status } shape handleAuthFailure expects.
      if (handleAuthFailure({ status: res.status })) return;
      const data = await res.json();
      setPromos(data);
      // Count only ready promos — matches the server's promo_count gating.
      onCountChange?.(channel.id, data.filter(p => p.status !== "processing" && p.status !== "failed").length);
    } catch {
      setError("Failed to load promos.");
    } finally {
      setLoading(false);
    }
  }, [channel?.id, onCountChange]);

  useEffect(() => {
    loadPromos();
    return () => { if (pollTimerRef.current) clearInterval(pollTimerRef.current); };
  }, [loadPromos]);

  // While any promo is still transcoding, refresh the list so its status flips
  // to ready/failed on its own — the user doesn't have to stay or reload.
  const hasProcessing = promos.some(p => p.status === "processing");
  useEffect(() => {
    if (!hasProcessing) return;
    const t = setInterval(loadPromos, 4000);
    return () => clearInterval(t);
  }, [hasProcessing, loadPromos]);

  // ── Load the selected promo for playback ──────────────────────────────────
  // Mint a short-lived, promo-scoped stream token, then point <video> at the
  // streaming route with that token. The browser streams with HTTP Range (seek
  // works, nothing is buffered fully into memory). No blob, no auth header.
  useEffect(() => {
    if (!playingPromo) return;
    let cancelled = false;
    setVideoUrl(null);
    setVideoError(null);
    setVideoLoading(true);
    (async () => {
      try {
        const res = await fetch(
          `${BASE}/channels/${channel.id}/promos/${playingPromo.id}/stream-token`,
          { headers: authHeaders() }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const { token } = await res.json();
        if (cancelled) return;
        setVideoUrl(`${BASE}/promo-stream/${playingPromo.id}?token=${encodeURIComponent(token)}`);
      } catch {
        if (!cancelled) setVideoError("Could not load the video.");
      } finally {
        if (!cancelled) setVideoLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [playingPromo, channel?.id]);

  // ── Upload ────────────────────────────────────────────────────────────────
  const handleFileSelected = async (file) => {
    if (!file) return;
    setShowUploadModal(false);
    setUploading(true);
    setUploadLabel(`Uploading ${file.name}…`);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("promo", file);

      const uploadRes = await fetch(`${BASE}/channels/${channel.id}/promo`, {
        method: "POST",
        body: formData,
        headers: authHeaders(),
      });
      if (!uploadRes.ok) {
        const err = await uploadRes.json();
        throw new Error(err.error || "Upload failed");
      }
      // The promo is now stored (status 'processing'). Drop the blocking upload
      // bar and show it in the list — the user is free to navigate away while
      // the background transcode runs; the list poll flips it to ready/failed.
      setUploading(false);
      setUploadLabel("");
      await loadPromos();

    } catch (err) {
      setUploading(false);
      setUploadLabel("");
      setError(err.message);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelected(e.dataTransfer.files[0]);
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (promo) => {
    setDeleteTarget(null);
    try {
      const res = await fetch(`${BASE}/channels/${channel.id}/promos/${promo.id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Delete failed");
      }
      await loadPromos();
    } catch (err) {
      setError(err.message);
    }
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const fmtSize = (bytes) => {
    if (!bytes) return "—";
    if (bytes >= 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };
  const fmtDate = (ts) => ts ? new Date(ts).toISOString().slice(0, 10) : "—";

  // ── Styles ────────────────────────────────────────────────────────────────
  const s = {
    page:  { padding: "28px 32px", fontFamily: "inherit", maxWidth: 900 },
    back:  { background: "none", border: "none", cursor: "pointer", color: "var(--text-sub)", fontSize: 13, padding: "0 0 20px 0", display: "flex", alignItems: "center", gap: 6 },
    label: { fontSize: 11, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 },
    val:   { fontSize: 13, color: "var(--text)", fontWeight: 500 },
    card:  { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, overflow: "hidden" },
  };

  return (
    <div style={s.page}>
      <button onClick={onBack} style={s.back}>← Back to Channel Management</button>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "var(--ink)", display: "flex", alignItems: "center", gap: 10 }}>
            {viewOnly ? "Promo Videos" : "Manage Promos"}
            {viewOnly && (
              <span style={{ fontSize: 11, background: "var(--surface-alt)", border: "1px solid var(--border)", borderRadius: 5, padding: "2px 8px", color: "var(--text-sub)", fontWeight: 500 }}>
                View Only
              </span>
            )}
          </div>
          <div style={{ fontSize: 13, color: "var(--text-sub)", marginTop: 4 }}>
            Channel: <strong style={{ color: "var(--ink)" }}>{channel?.display_name || channel?.id}</strong>
            {channel?.channel_number && <span style={{ color: "var(--text-dim)", marginLeft: 8 }}>· Ch {channel.channel_number}</span>}
            {channel?.quality        && <span style={{ color: "var(--text-dim)", marginLeft: 8 }}>· {channel.quality}</span>}
          </div>
        </div>
        {!viewOnly && (
          <button
            onClick={() => setShowUploadModal(true)}
            disabled={uploading}
            style={{
              background: uploading ? "var(--surface-alt)" : "var(--ink)",
              border: "none", borderRadius: 8, padding: "9px 18px",
              fontSize: 13, fontWeight: 600,
              color: uploading ? "var(--text-dim)" : "#fff",
              cursor: uploading ? "not-allowed" : "pointer", fontFamily: "inherit",
            }}
          >
            ↑ Upload Promo
          </button>
        )}
      </div>

      {/* Channel info strip */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 24 }}>
        {[
          { label: "Channel ID",  val: channel?.id },
          { label: "Source IP",   val: channel?.source_ip   || "—" },
          { label: "Port",        val: channel?.source_port || "—" },
          { label: "Bitrate",     val: channel?.video_bitrate ? `${channel.video_bitrate} Mbps` : "—" },
          { label: "Status",      val: channel?.status || "—" },
        ].map(({ label, val }) => (
          <div key={label} style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, padding: "10px 16px", minWidth: 100 }}>
            <div style={s.label}>{label}</div>
            <div style={s.val}>{val}</div>
          </div>
        ))}
      </div>

      {/* Error banner */}
      {error && (
        <div style={{ background: "var(--red-bg)", border: "1px solid var(--red-border)", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "var(--red)", marginBottom: 16, display: "flex", justifyContent: "space-between" }}>
          {error}
          <button onClick={() => setError(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--red)", fontSize: 16 }}>×</button>
        </div>
      )}

      {/* Upload progress */}
      {uploading && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, padding: "12px 16px", marginBottom: 16 }}>
          <span style={{ display: "inline-block", width: 14, height: 14, border: "2px solid var(--border)", borderTopColor: "var(--ink)", borderRadius: "50%", animation: "promo-spin 0.7s linear infinite" }} />
          <span style={{ fontSize: 13, color: "var(--text-sub)" }}>{uploadLabel}</span>
        </div>
      )}

      {/* Promo list */}
      {loading ? (
        <div style={{ ...s.card, padding: "40px 24px", textAlign: "center", color: "var(--text-dim)", fontSize: 13 }}>Loading…</div>
      ) : promos.length === 0 && !uploading ? (
        <div style={{ ...s.card, padding: "48px 24px", textAlign: "center", color: "var(--text-dim)", fontSize: 13 }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🎬</div>
          No promo videos uploaded yet.
          {!viewOnly && (
            <div style={{ marginTop: 10 }}>
              <button onClick={() => setShowUploadModal(true)} style={{ background: "none", border: "none", color: "var(--blue, #2563eb)", fontSize: 13, cursor: "pointer", textDecoration: "underline", fontFamily: "inherit" }}>
                Upload your first promo
              </button>
            </div>
          )}
        </div>
      ) : (
        <div style={s.card}>
          {/* Table header */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 100px 90px 120px auto", gap: 12, padding: "10px 18px", borderBottom: "1px solid var(--border)", background: "var(--surface-alt)" }}>
            {["File Name", "Size", "Duration", "Uploaded", ""].map((h, i) => (
              <div key={i} style={{ fontSize: 11, fontWeight: 600, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{h}</div>
            ))}
          </div>

          {promos.map((promo, idx) => {
            const isProcessing = promo.status === "processing";
            const isFailed     = promo.status === "failed";
            const isReady      = !isProcessing && !isFailed;
            // Only ready promos count toward the "at least 1 must remain" rule.
            const readyCount = promos.filter(p => p.status !== "processing" && p.status !== "failed").length;
            const isLast = isReady && readyCount === 1;
            return (
              <div key={promo.id} style={{ display: "grid", gridTemplateColumns: "1fr 100px 90px 120px auto", gap: 12, padding: "13px 18px", borderBottom: idx < promos.length - 1 ? "1px solid var(--border)" : "none", alignItems: "center", opacity: isProcessing ? 0.7 : 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
                  <span style={{ fontSize: 18, flexShrink: 0 }}>🎬</span>
                  <span style={{ fontSize: 13, fontWeight: 500, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{promo.original_name || promo.filename}</span>
                  {isProcessing && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, flexShrink: 0, fontSize: 11, fontWeight: 600, color: "var(--text-sub)", background: "var(--surface-alt)", border: "1px solid var(--border)", borderRadius: 5, padding: "2px 8px" }}>
                      <span style={{ display: "inline-block", width: 9, height: 9, border: "2px solid var(--border)", borderTopColor: "var(--ink)", borderRadius: "50%", animation: "promo-spin 0.7s linear infinite" }} />
                      Processing
                    </span>
                  )}
                  {isFailed && (
                    <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 600, color: "var(--red, #dc2626)", background: "var(--red-bg, #fef2f2)", border: "1px solid var(--red-border, #fecaca)", borderRadius: 5, padding: "2px 8px" }}>Failed</span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: "var(--text-sub)" }}>{fmtSize(promo.size_bytes)}</div>
                <div style={{ fontSize: 12, color: "var(--text-sub)" }}>{promo.duration || "—"}</div>
                <div style={{ fontSize: 12, color: "var(--text-sub)" }}>{fmtDate(promo.uploaded_at)}</div>
                <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                  <button
                    onClick={() => isReady && setPlayingPromo(promo)}
                    disabled={!isReady}
                    title={isReady ? "Play" : "Not ready yet"}
                    style={{ background: "var(--green-bg, #f0fdf4)", border: "1px solid var(--green-border, #bbf7d0)", borderRadius: 6, padding: "5px 10px", fontSize: 13, color: "var(--green, #16a34a)", cursor: isReady ? "pointer" : "not-allowed", lineHeight: 1, opacity: isReady ? 1 : 0.4 }}
                  >▶</button>
                  {!viewOnly && (
                    <button
                      onClick={() => !isLast && setDeleteTarget(promo)}
                      title={isLast ? "At least 1 promo must remain" : "Delete"}
                      style={{ background: isLast ? "var(--surface)" : "var(--red-bg, #fef2f2)", border: `1px solid ${isLast ? "var(--border)" : "var(--red-border, #fecaca)"}`, borderRadius: 6, padding: "5px 10px", fontSize: 12, color: isLast ? "var(--text-dim)" : "var(--red, #dc2626)", cursor: isLast ? "not-allowed" : "pointer", opacity: isLast ? 0.5 : 1 }}
                    >Delete</button>
                  )}
                </div>
              </div>
            );
          })}

          <div style={{ padding: "10px 18px", borderTop: "1px solid var(--border)", fontSize: 12, color: "var(--text-dim)", background: "var(--surface-alt)" }}>
            {(() => {
              const ready = promos.filter(p => p.status !== "processing" && p.status !== "failed").length;
              const pending = promos.length - ready;
              return `${ready} promo${ready !== 1 ? "s" : ""}${pending ? ` · ${pending} processing` : ""} · Minimum 1 required`;
            })()}
          </div>
        </div>
      )}

      {/* ── Upload Modal ── */}
      {showUploadModal && (
        <div onClick={() => setShowUploadModal(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, padding: "32px 28px", width: 420, maxWidth: "90vw", boxShadow: "0 8px 40px rgba(0,0,0,0.18)", fontFamily: "inherit" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "var(--ink)" }}>Upload Promo</div>
                <div style={{ fontSize: 12, color: "var(--text-sub)", marginTop: 3 }}>
                  {channel?.display_name}{channel?.channel_number && ` · Ch ${channel.channel_number}`}
                </div>
              </div>
              <button onClick={(e) => { e.stopPropagation(); setShowUploadModal(false); }} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-dim)", fontSize: 22, lineHeight: 1, padding: "4px 6px", borderRadius: 4 }}>×</button>
            </div>

            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current.click()}
              style={{ border: `2px dashed ${dragOver ? "var(--ink)" : "var(--border)"}`, borderRadius: 10, padding: "36px 20px", textAlign: "center", cursor: "pointer", background: dragOver ? "var(--surface-alt)" : "transparent", transition: "all 0.15s", marginTop: 20, marginBottom: 16 }}
            >
              <div style={{ fontSize: 36, marginBottom: 10 }}>📁</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", marginBottom: 6 }}>{dragOver ? "Drop to upload" : "Drag & drop your video here"}</div>
              <div style={{ fontSize: 12, color: "var(--text-dim)", marginBottom: 16 }}>or</div>
              <div style={{ display: "inline-block", background: "var(--ink)", color: "#fff", borderRadius: 7, padding: "8px 20px", fontSize: 12, fontWeight: 600 }}>Browse file</div>
              <div style={{ fontSize: 11, color: "var(--text-dim)", marginTop: 14 }}>MP4, MOV, AVI, MKV, WEBM · Max 600 MB</div>
            </div>

            <button onClick={() => setShowUploadModal(false)} style={{ width: "100%", background: "none", border: "1px solid var(--border)", borderRadius: 7, padding: "8px 0", fontSize: 12, color: "var(--text-sub)", cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
          </div>
        </div>
      )}

      <input ref={fileInputRef} type="file" accept="video/mp4,video/quicktime,video/x-msvideo,video/x-matroska,video/webm" style={{ display: "none" }} onChange={e => { handleFileSelected(e.target.files[0]); fileInputRef.current.value = ""; }} />

      {/* ── Delete Confirm Modal ── */}
      {deleteTarget && (
        <div onClick={() => setDeleteTarget(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: "28px", width: 380, maxWidth: "90vw", boxShadow: "0 8px 40px rgba(0,0,0,0.18)", fontFamily: "inherit" }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--ink)", marginBottom: 8 }}>Delete Promo?</div>
            <div style={{ fontSize: 13, color: "var(--text-sub)", marginBottom: 24, lineHeight: 1.6 }}>
              Are you sure you want to delete <strong style={{ color: "var(--text)" }}>{deleteTarget.filename}</strong>? This cannot be undone.
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => handleDelete(deleteTarget)} style={{ flex: 1, background: "var(--red, #dc2626)", border: "none", borderRadius: 8, padding: "9px 0", fontSize: 13, fontWeight: 600, color: "#fff", cursor: "pointer", fontFamily: "inherit" }}>Delete</button>
              <button onClick={() => setDeleteTarget(null)} style={{ flex: 1, background: "none", border: "1px solid var(--border)", borderRadius: 8, padding: "9px 0", fontSize: 13, color: "var(--text-sub)", cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Video Player Modal ── */}
      {playingPromo && (
        <div onClick={() => setPlayingPromo(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.80)", zIndex: 3000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "#000", borderRadius: 12, overflow: "hidden", maxWidth: "88vw", maxHeight: "88vh", width: 820, boxShadow: "0 12px 60px rgba(0,0,0,0.7)", display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: "#111", borderBottom: "1px solid #222" }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>{channel?.display_name}</div>
                <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>{playingPromo.filename}</div>
              </div>
              <button onClick={() => setPlayingPromo(null)} style={{ background: "#222", border: "1px solid #333", borderRadius: 6, cursor: "pointer", color: "#aaa", fontSize: 18, lineHeight: 1, padding: "4px 10px" }}>×</button>
            </div>
            {videoLoading ? (
              <div style={{ padding: "60px 24px", textAlign: "center", color: "#888", fontSize: 13 }}>
                <div style={{ width: 28, height: 28, border: "3px solid #333", borderTopColor: "#888", borderRadius: "50%", margin: "0 auto 14px", animation: "promo-spin 0.8s linear infinite" }} />
                Loading video…
              </div>
            ) : videoError ? (
              <div style={{ padding: "60px 24px", textAlign: "center", color: "#c77", fontSize: 13 }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
                {videoError}<br />
                <span style={{ color: "#666", fontSize: 12 }}>File: {playingPromo.filename}</span>
              </div>
            ) : (
              <video src={videoUrl} controls autoPlay style={{ width: "100%", maxHeight: "72vh", display: "block", background: "#000" }} />
            )}
          </div>
        </div>
      )}

      <style>{`@keyframes promo-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
